import { Fab, Slide } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import { UserPermissions } from '../../services/permissions'
import { getNewLocation } from '../../utils'
import { getHasTomDevices } from '../root/actions'
import { Button, Modal, Tabs } from '../root/components/common'
import { LocationDevicesDot } from '../root/components/common/location-devices-dot/locations-devices-dot'
import { LocationsTreeNode } from '../root/components/common/locations-tree/locations-tree-node'
import { useTreeUtils } from '../root/components/common/locations-tree/tree-utils'
import { useLocationsDevicesAmount } from '../root/hooks/use-locations-devices-amount'
import * as actions from './actions'
import { LocationsTree } from './components/locations-tree'
import {
    LOCATIONS_TREE_ROOT_NODE_ID,
    LOCATION_TABS,
    NEW_LOCATION_ID
} from './constants'
import DevicesToFloorPlan from './devices-to-floor-plan'
import DevicesToMove from './devices-to-move'
import LocationDetails from './location-details'
import {
    getLocationsTreeSelector,
    getPickerLocationsSelector
} from './selectors'
import { Add as AddIcon } from '@material-ui/icons'
import { DialogOpener } from '../root/components/common/dialog-opener'
import { LocationCreateModal } from './components/location-create-modal'
import { isEmpty } from 'lodash'

const useStyles = makeStyles(theme => ({
    wrapper: {
        display: 'flex',
        height: 'calc(100% + 50px)',
        position: 'relative',
        marginTop: -25,
        marginBottom: -25,
        marginLeft: -50,
        marginRight: -50
    },
    noLocationSelected: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%'
    },
    devicesToMoveWrapper: {
        position: 'absolute',
        height: '100%',
        width: 383,
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 25,
        paddingBottom: 25,
        backgroundColor: theme.background,
        zIndex: 999,
        overflow: 'auto',
        borderRight: theme.border
    },
    devicesToFloorPlanWrapper: {
        position: 'absolute',
        height: '100%',
        width: '25%',
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 25,
        paddingBottom: 25,
        backgroundColor: theme.background,
        zIndex: 999,
        overflow: 'auto',
        borderRight: theme.border
    },
    locationsTreeWrapper: {
        flexBasis: 383,
        borderRight: theme.border,
        paddingLeft: 12,
        paddingTop: 20,
        '@media (max-width: 1279px)': {
            flexBasis: 283
        },
        position: 'relative'
        /* paddingBottom: 25, */
        // overflow: "auto",
    },
    locationDetailWrapper: {
        paddingLeft: 37,
        paddingRight: 37,
        paddingTop: 25,
        paddingBottom: 25,
        flex: 3,
        overflow: 'auto'
    },
    locationsTree: {
        fontFamily: theme.fontFamily
    },
    locationsTreeGroup: {
        marginLeft: 8
    },
    locationsTreeLabelCustomWrapper: {
        display: 'flex',
        justifyContent: 'space-between',
        // width: '100%',
        paddingRight: 12,
        alignItems: 'center'
    },
    locationsTreeLabel: {
        cursor: 'pointer',
        overflow: 'hidden',
        fontSize: 13,
        minHeight: 37,
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        '&:hover': {
            backgroundColor: theme.secondary,
            borderTopLeftRadius: theme.borderRadius,
            borderBottomLeftRadius: theme.borderRadius
        }
    },
    locationsTreeLabelContent: {
        position: 'relative',
        minHeight: 37,
        borderTopLeftRadius: theme.borderRadius,
        borderBottomLeftRadius: theme.borderRadius,
        '&:hover': {
            backgroundColor: theme.secondary,
            borderTopLeftRadius: theme.borderRadius,
            borderBottomLeftRadius: theme.borderRadius,
            '& button': {
                display: 'inline-flex'
            }
        },
        '& button': {
            display: 'none'
        }
    },
    locationsTreeLabelSelected: {
        fontWeight: 'bold',
        backgroundColor: theme.primary,
        color: theme.textPrimaryReverted,
        '& > div > div': {
            fontWeight: 'bold'
        }
    },
    locationsTreeIconContainer: {
        marginLeft: 3
    },

    modalButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 15
    },
    locationsWrapper: {
        width: '100%',
        height: 'calc(100% - 128px)'
    },
    treeItemIcon: {
        width: 15,
        marginLeft: 3
    },
    toggleMyLocations: {
        height: 40,
        display: 'flex',
        alignItems: 'center',
        marginBottom: 8,
        marginLeft: 8,
        '&& span': {
            fontSize: 12
        }
    },
    respIcon: {
        position: 'absolute',
        left: '-11px',
        color: theme.active,
        fontWeight: 'bold',
        fontSize: 18
    },
    tooltip: {
        backgroundColor: '#FFF',
        fontSize: 13,
        color: theme.primary,
        textAlign: 'middle'
    },
    tooltipDot: {
        fontSize: 18,
        color: theme.active,
        display: 'inline-block',
        lineHeight: '18px',
        verticalAlign: 'text-top'
    },
    tabs: {
        marginBottom: 16
    },
    addFab: {
        position: 'absolute',
        bottom: 20,
        right: 20,
        backgroundColor: theme.primary,
        color: theme.textPrimaryReverted
    }
}))

function Locations() {
    const classes = useStyles()
    const dispatch = useDispatch()
    const { id } = useParams()

    const {
        isLocationsSidebarLoading,
        location,
        isAddDevicesToFloorPlanMode,
        locations
    } = useSelector(state => state.locations)

    const { user, locale, theme } = useSelector(state => state.main)
    const {
        locationsMap,
        locationsTree,
        assignedLocationsTree,
        assignedLocationsMap
    } = useSelector(getLocationsTreeSelector)

    const pickerLocations = useSelector(getPickerLocationsSelector)
    const treeUtils = useTreeUtils(locationsTree)
    const assignedTreeUtils = useTreeUtils(assignedLocationsTree)
    const [currentTab, setCurrentTab] = useState(LOCATION_TABS.my)
    const isMyLocationsTab = currentTab === LOCATION_TABS.my
    const [selectedLocation, setSelectedLocation] = useState(
        (location && location.id) || LOCATIONS_TREE_ROOT_NODE_ID
    )

    const [isConfirmFromNewModalOpen, setIsConfirmFromNewModalOpen] =
        useState(false)
    const [isDeleteLocationsModalOpen, setIsDeleteLocationsModalOpen] =
        useState(false)
    const [locationsToDelete, setLocationsToDelete] = useState([])
    const [locationToNavigateAfterNew, setLocationToNavigateAfterNew] =
        useState(null)
    const [isDevicesToMoveMode, setIsDevicesToMoveMode] = useState(false)
    const [isNetworkDevicesToMoveMode, setIsNetworkDevicesToMoveMode] =
        useState(false)

    const [searchValue, setSearchValue] = useState('')

    const {
        devicesAmountMap,
        isLoadingDevicesAmountMap,
        locationsDevicesAmountMap
    } = useLocationsDevicesAmount(searchValue)

    const treeRef = useRef(null)
    const assignedTreeRef = useRef(null)

    const history = useHistory()

    const tabs = {
        [LOCATION_TABS.my]: {
            key: 'my',
            name: locale.locations.myLocations
        },
        [LOCATION_TABS.all]: {
            key: 'all',
            name: locale.locations.allLocations
        }
    }

    const isAddNewLocationMode =
        useLocation().pathname.includes(NEW_LOCATION_ID)

    const permissions = {
        isTraplinkedAdmin: UserPermissions.isTraplinkedAdmin(),
        createLocations: UserPermissions.location().create,
        createLocation: id => UserPermissions.location(id).create,
        updateLocation: id => UserPermissions.location(id).update,
        deleteLocation: id => UserPermissions.location(id).delete,
        onlyRead(id) {
            return (
                !this.createLocation(id) &&
                !this.updateLocation(id) &&
                !this.deleteLocation(id)
            )
        },
        share: id =>
            user.original_customer_id === id &&
            UserPermissions.device(user.original_customer_id).create &&
            UserPermissions.location(user.original_customer_id).create
    }

    const onAddSubLocation = locationId => {
        const parentLocation = isMyLocationsTab
            ? assignedLocationsMap[locationId]
            : locationsMap[locationId]
        const newLocation = getNewLocation({
            customer_id: parentLocation.location.customer_id,
            owner_id: parentLocation.location.owner_id,
            parent_id: locationId,
            locale
        })
        if (isMyLocationsTab) {
            assignedTreeUtils.addChild(
                { id: newLocation.id, location: newLocation },
                locationId
            )
        } else {
            treeUtils.addChild(
                { id: newLocation.id, location: newLocation },
                locationId
            )
        }

        dispatch(actions.locationRequestSuccess(newLocation))
        onLocationLabelClick(newLocation.id)
    }

    const deleteLocations = locations => {
        dispatch(actions.locationsDeleteRequest({ locations }))
        setIsDeleteLocationsModalOpen(false)
        setSelectedLocation(LOCATIONS_TREE_ROOT_NODE_ID)
        history.push('/locations')
    }

    const renderTreeNode = props => (
        <LocationsTreeNode
            search={searchValue}
            permissions={permissions}
            user={user}
            onLocationPress={onLocationLabelClick}
            onAddSubLocation={onAddSubLocation}
            setIsDevicesToMoveMode={setIsDevicesToMoveMode}
            setIsNetworkDevicesToMoveMode={setIsNetworkDevicesToMoveMode}
            locale={locale}
            setIsDeleteLocationsModalOpen={setIsDeleteLocationsModalOpen}
            setLocationsToDelete={setLocationsToDelete}
            actionDisabled={
                isAddNewLocationMode ||
                permissions.onlyRead(props?.node?.data?.location?.customer_id)
            }
            rightSlot={location => <LocationDevicesDot location={location} />}
            theme={theme}
            isMyLocationsTab={isMyLocationsTab}
            pickerLocations={pickerLocations}
            isLoadingDevicesAmountMap={isLoadingDevicesAmountMap}
            devicesAmountMap={devicesAmountMap}
            locationsDevicesAmountMap={locationsDevicesAmountMap}
            {...props}
        />
    )

    const onLocationLabelClick = locationId => {
        if (locationId === LOCATIONS_TREE_ROOT_NODE_ID) {
            history.push(`/locations`)
            setSelectedLocation(locationId)
            return
        }
        if (
            locationId !== LOCATIONS_TREE_ROOT_NODE_ID &&
            isAddNewLocationMode
        ) {
            setIsConfirmFromNewModalOpen(true)
            setLocationToNavigateAfterNew(locationId)
            return
        }

        setSelectedLocation(locationId)

        if (locationId !== LOCATIONS_TREE_ROOT_NODE_ID) {
            const currentPath = history.location.pathname
                .split('/')
                .slice(-1)[0]
            const currentTab =
                currentPath === 'locations' ? 'overview' : currentPath
            history.push(`/locations/${locationId}/${currentTab}`)
        }
    }

    const cancelSaveNewLocation = () => {
        onLocationLabelClick(locationToNavigateAfterNew)
        history.goBack()
        treeUtils.deleteNode(NEW_LOCATION_ID)

        setLocationToNavigateAfterNew(null)
        // dispatch(actions.locationsRequest());
        setIsConfirmFromNewModalOpen(false)
    }

    const onCancelNavigateFromNew = () => {
        setLocationToNavigateAfterNew(null)
        setIsConfirmFromNewModalOpen(false)
    }

    const onSearch = search => {
        // TODO: use another state variable for picker locations
        setSearchValue(search)
        return dispatch(actions.locationsRequest({ search }))
    }

    useEffect(() => {
        dispatch(getHasTomDevices())
        dispatch(actions.locationsRequest())
        setSearchValue('')
        return () => {
            dispatch(actions.locationsRequestSuccess(null))
        }
    }, [currentTab])

    return (
        <div className={classes.wrapper}>
            <Slide
                direction="right"
                in={isDevicesToMoveMode || isNetworkDevicesToMoveMode}
                timeout={500}
                mountOnEnter
                unmountOnExit>
                <div className={classes.devicesToMoveWrapper}>
                    {isDevicesToMoveMode && (
                        <DevicesToMove
                            setIsDevicesToMoveMode={setIsDevicesToMoveMode}
                            selectedLocation={selectedLocation}
                        />
                    )}
                    {isNetworkDevicesToMoveMode && (
                        <DevicesToMove
                            setIsDevicesToMoveMode={
                                setIsNetworkDevicesToMoveMode
                            }
                            selectedLocation={selectedLocation}
                            deviceType="network"
                        />
                    )}
                </div>
            </Slide>
            <Slide
                direction="right"
                in={isAddDevicesToFloorPlanMode}
                timeout={500}
                mountOnEnter
                unmountOnExit>
                <div className={classes.devicesToMoveWrapper}>
                    {isAddDevicesToFloorPlanMode && <DevicesToFloorPlan />}
                </div>
            </Slide>
            <div className={classes.locationsTreeWrapper}>
                <Tabs
                    className={classes.tabs}
                    tabs={tabs}
                    currentTab={currentTab}
                    setCurrentTab={setCurrentTab}
                />
                {currentTab === LOCATION_TABS.all &&
                    !isDevicesToMoveMode &&
                    !isNetworkDevicesToMoveMode &&
                    !isAddDevicesToFloorPlanMode && (
                        <LocationsTree
                            currentLocation={id}
                            treeRef={treeRef}
                            onSearch={onSearch}
                            isLoading={isLocationsSidebarLoading}
                            treeUtils={treeUtils}
                            renderTreeNode={renderTreeNode}
                            searchStr={searchValue}
                            emptyText={locale.locations.noLocations}
                        />
                    )}

                {currentTab === LOCATION_TABS.my &&
                    !isDevicesToMoveMode &&
                    !isNetworkDevicesToMoveMode &&
                    !isAddDevicesToFloorPlanMode && (
                        <LocationsTree
                            currentLocation={id}
                            treeRef={assignedTreeRef}
                            onSearch={onSearch}
                            isLoading={isLocationsSidebarLoading}
                            treeUtils={assignedTreeUtils}
                            renderTreeNode={renderTreeNode}
                            searchStr={searchValue}
                            emptyText={
                                locale.locations.noLocationsForResponsible
                            }
                        />
                    )}
                {!isDevicesToMoveMode &&
                    !isNetworkDevicesToMoveMode &&
                    !isAddDevicesToFloorPlanMode &&
                    permissions.createLocations &&
                    !isLocationsSidebarLoading &&
                    !isEmpty(locations) && (
                        <DialogOpener
                            dialog={(handleClose, modalProps) => {
                                return modalProps.open ? (
                                    <LocationCreateModal
                                        onClose={() => {
                                            handleClose()
                                        }}
                                        modalProps={modalProps}
                                        withCustomerSelection
                                    />
                                ) : null
                            }}>
                            {handleOpen => (
                                <Fab
                                    color="primary"
                                    className={classes.addFab}
                                    onClick={handleOpen}>
                                    <AddIcon />
                                </Fab>
                            )}
                        </DialogOpener>
                    )}
            </div>
            <div className={classes.locationDetailWrapper}>
                {id ? (
                    <LocationDetails
                        onAddSubLocation={onAddSubLocation}
                        locationId={id}
                        setSelectedLocation={setSelectedLocation}
                        pickerLocations={pickerLocations}
                        selectLocation={onLocationLabelClick}
                        selectedLocation={selectedLocation}
                        isDevicesToMoveMode={isDevicesToMoveMode}
                        setIsDevicesToMoveMode={setIsDevicesToMoveMode}
                        isNetworkDevicesToMoveMode={isNetworkDevicesToMoveMode}
                        setIsNetworkDevicesToMoveMode={
                            setIsNetworkDevicesToMoveMode
                        }
                    />
                ) : (
                    <div className={classes.noLocationSelected}>
                        {locale.locations.pleaseSelectLocation}
                    </div>
                )}
            </div>

            <Modal
                open={isConfirmFromNewModalOpen}
                setOpen={setIsConfirmFromNewModalOpen}>
                <div>
                    <div>{locale.locations.sureNotToSaveLocation}</div>
                    <div className={classes.modalButtons}>
                        <Button onClick={cancelSaveNewLocation}>
                            {locale.common.ok}
                        </Button>
                        <Button reverted onClick={onCancelNavigateFromNew}>
                            {locale.common.cancel}
                        </Button>
                    </div>
                </div>
            </Modal>

            <Modal
                open={isDeleteLocationsModalOpen}
                setOpen={setIsDeleteLocationsModalOpen}>
                <div>{locale.locations.deleteLocationsModalText}</div>
                <div className={classes.modalButtons}>
                    <Button
                        onClick={deleteLocations.bind(this, locationsToDelete)}>
                        {locale.common.delete}
                    </Button>
                    <Button
                        reverted
                        onClick={setIsDeleteLocationsModalOpen.bind(
                            this,
                            false
                        )}>
                        {locale.common.cancel}
                    </Button>
                </div>
            </Modal>
        </div>
    )
}

export default Locations
